import { ERP_BASE, logApiBaseOnce } from "../config/apiBase";
import {
  clearAuthStorage,
  getStoredAuthProfile,
  getStoredToken,
  isTokenExpired,
  setAuthSession,
} from "./auth";

const AUTH_SESSION_VALIDATE_TIMEOUT_MS = 3000;

const shouldDeleteCache = (name) =>
  /(workbox|precache|runtime|booster)/i.test(String(name || ""));

const unregisterServiceWorkers = async () => {
  if (!("serviceWorker" in navigator)) return;
  try {
    const registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations.map((registration) => registration.unregister()));
  } catch (error) {
    // best-effort cleanup only
  }
};

const cleanupStaleCaches = async () => {
  if (!("caches" in window)) return;
  try {
    const keys = await caches.keys();
    const staleKeys = keys.filter(shouldDeleteCache);
    await Promise.all(staleKeys.map((key) => caches.delete(key)));
  } catch (error) {
    // best-effort cleanup only
  }
};

export const validateStoredSession = async (token) => {
  if (!token) return { status: "valid", user: null };
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), AUTH_SESSION_VALIDATE_TIMEOUT_MS);

  try {
    const response = await fetch(`${ERP_BASE}/api/session`, {
      method: "GET",
      cache: "no-store",
      headers: {
        Authorization: `Bearer ${token}`,
        "Cache-Control": "no-store, no-cache, must-revalidate",
        Pragma: "no-cache",
      },
      signal: controller.signal,
    });

    if (response.status === 401) {
      return { status: "invalid", user: null };
    }

    if (!response.ok) return { status: "unknown", user: null };
    const payload = await response.json();
    return { status: "valid", user: payload?.user || null };
  } catch (error) {
    // Do not hard-logout on transient network issues.
    return { status: "unknown", user: null };
  } finally {
    clearTimeout(timeoutId);
  }
};

export const initializeAppRuntime = async () => {
  logApiBaseOnce();
  const token = getStoredToken();
  if (token && isTokenExpired(token)) {
    clearAuthStorage();
  } else if (token) {
    const session = await validateStoredSession(token);
    if (session.status === "invalid") {
      clearAuthStorage();
    } else if (session.status === "valid" && session.user) {
      const current = getStoredAuthProfile();
      const user = session.user;
      setAuthSession({
        ...current,
        token,
        user: JSON.stringify(user),
        name: user.name || user.displayName || current.name || "",
        className: user.className || user.class || current.className || "",
        role: user.role || "",
        selectedSchoolCode: user.selectedSchoolCode || user.schoolCode || "",
        selectionRequired: user.selectionSource === "selection_required",
        memberships: Array.isArray(user.memberships) ? user.memberships : [],
      });
    }
  }

  // Non-blocking cleanup for stale PWA artifacts from older deployments.
  unregisterServiceWorkers();
  cleanupStaleCaches();
};
